import axios from 'axios';
import moment from 'moment';
import _ from 'lodash';
import i18n from '../i18n';

import ClientStorage from '../util/ClientStorage';
import { trimResourceNames, checkAppUpdate } from '../util/helpers';
import { APP_UPDATE_COOKIE_NAME, APP_UPDATE_EXPIRE_TIME } from '../util/constants';

const acuteTimeFormat = 'YYYY-MM-DD';
const hashedTokenHeaderName = 'X-UIDH';

const getHashedHeader = hashedApiToken => Object.assign({ }, { headers: { [hashedTokenHeaderName]: hashedApiToken } });

const checkForError = (response) => {
  // check for updates
  checkAppUpdate(response);
  if (response.status < 200 || response.status >= 300) {
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
  return response;
};

let updateResolver;
const triggerAppUpdate = () => {
  if (ClientStorage.getAppBuildTimestamp() && !ClientStorage.isAppUpdateInProgress()) {
    ClientStorage.setAppUpdateInProgress();
    document.cookie = `${APP_UPDATE_COOKIE_NAME}=;expires=${APP_UPDATE_EXPIRE_TIME};`;
    // release the semaphore
    ClientStorage.removeAppBuildTimestamp();
    // clear the app update timestamp
    ClientStorage.clearAppUpdateInProgress();
    updateResolver = new Promise((resolve, reject) => {
      console.log('>>updating...<<');
      // set a timeout
      resolve(true);
    });
    // Force a reload
    updateResolver
      .then(() => {
        // Force a reload
        window.location.reload(true);
      });
  }
};

// const getData = response => response.data;
const getData = (response) => {
  console.log('Received', response.data);
  triggerAppUpdate();
  return response.data || [];
};

const getTS = () => moment().unix();

const addTS = (url) => {
  const ts = getTS();
  return url.indexOf('?') === -1 ? `${url}?ts=${ts}` : `${url}&ts=${ts}`;
};

const checkUser = () => axios.get(addTS('/api/v1/account/ehr'))
.then(checkForError)
.then(getData)
.then((user) => {
  if (!user || !user.sotu) {
    throw new Error('Invalid user data!');
  }
  return user;
});

const applyAcuteStagingConfigToConfig = (config, stagingConfig) => {
  let modifiedConfig = config;
  let configStr = JSON.stringify(config);

  // Replace group data if exists
  if (stagingConfig.groups) {
    Object.entries(stagingConfig.groups).forEach(
      ([key, value]) => {
        console.log(key, value);
        configStr = configStr.replace(new RegExp(key, 'g'), value);
      }
    );
    modifiedConfig = JSON.parse(configStr);
  }

  return modifiedConfig;
};
const requestOrganization = axios.get(addTS('/api/v1/anonymous/organization'))
  .then(checkForError)
  .then(getData)
  .then((organization) => {
    if (!organization) {
      throw new Error('Invalid organization data!');
    }
    const currentLang = i18n.language;
    if (!currentLang) {
      const defaultLocale = organization.defaultLocale;
    
      if (defaultLocale) {
        const lang = defaultLocale.split('_')[0];
        i18n.changeLanguage(lang);
      } else {
        console.warn('No defaultLocale found, using fallback');
        i18n.changeLanguage('fi');
      }
    } 
    return organization;
  });

const addSelectedEmployer = (employerId, originalUrl) => {
  if (employerId) {
    return originalUrl.indexOf('?') === -1 ? `${originalUrl}?employer=${employerId}` : `${originalUrl}&employer=${employerId}`;
  }
  return `${originalUrl}`;
};

const requestAllNamespaces = (language, namespaces, employerId) => new Promise((resolve, reject) => {
  axios.all(
    namespaces.map(
      // new proposed format:
      // namespace => axios.get(addTS(`/api/v1/anonymous/resource?file=i18n/${language}/${namespace}.json`))
      // previous format:
      // namespace => axios.get(addTS(addSelectedEmployer(employerId, `/api/v1/anonymous/resource/i18n/${language}/${namespace}.json`)))
      namespace => axios.get(addTS(addSelectedEmployer(employerId, `/api/v1/anonymous/resource?file=i18n/${language}/${namespace}.json`)))
      .then(checkForError)
      .then(getData)
      .then(namespaceData => Object.assign({ namespaceName: namespace, namespaceData }))
    )
  )
  .then(axios.spread((...localizations) => { resolve(localizations) }))
  .catch((error) => { reject(error) });
});

const requestAllNamespacesForLanguage = (language, namespaces, employerId) => new Promise((resolve, reject) => {
  requestAllNamespaces(language, namespaces, employerId)
    .then(namespaceMap => Object.assign({ language, namespaceMap }))
    .then((localizationsByLanguage) => { resolve(localizationsByLanguage) })
    .catch((error) => { reject(error) });
});

const requestAllByLanguages = (languages, namespaces, employerId) => new Promise((resolve, reject) => {
  Promise.all(
    languages.map(
      language => requestAllNamespacesForLanguage(language, namespaces, employerId)
    )
  )
  .then((localizationsByLanguage) => { resolve(localizationsByLanguage) })
  .catch((error) => { reject(error) });
});


const getHashedApiToken = () => axios.get(addTS('/api/v1/hashed/request'))
  .then(checkForError)
  .then(getData);

// generic function to fetch from a url with custom headers
const requestPromise = (url, headers) => axios.get(addTS(url), headers);

// resources are EHR professionals
const requestResources = () => new Promise((resolve, reject) => {
  requestPromise('/api/v1/resources', { })
    .then(checkForError)
    .then(getData)
    .then(data => trimResourceNames(data))
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
});

const requestResourcesThroughHashedApi = () => new Promise((resolve, reject) => {
  getHashedApiToken()
  .then((hashedApiToken) => {
    requestPromise('/api/v1/hashed/resources', getHashedHeader(hashedApiToken))
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(trimResourceNames(data)) })
    .catch((error) => { reject(error) });
  })
  .catch((error) => { reject(error) });
});

const getRequestResourcesWrapper = (userIdentified) => {
  if (userIdentified) {
    return requestResources();
  }
  return requestResourcesThroughHashedApi();
};

// units are sites or specific where they give consultation or an organization unit
const requestUnits = legalEntity => new Promise((resolve, reject) => {
  const urlPrefix = '/api/v1/units';
  const legalEntityParam = legalEntity ?
    `?legalEntity=${legalEntity}` :
    '';
  requestPromise(`${urlPrefix}${legalEntityParam}`, { })
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
});

const requestUnitsThroughHashedApi = legalEntity => new Promise((resolve, reject) => {
  const urlPrefix = '/api/v1/hashed/units';
  const legalEntityParam = legalEntity ?
    `?legalEntity=${legalEntity}` :
    '';
  getHashedApiToken()
  .then((hashedApiToken) => {
    requestPromise(`${urlPrefix}${legalEntityParam}`, getHashedHeader(hashedApiToken))
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
  })
  .catch((error) => { reject(error) });
});

const getRequestUnitsWrapper = (userIdentified, legalEntity) => {
  if (userIdentified) {
    return requestUnits(legalEntity);
  }
  return requestUnitsThroughHashedApi(legalEntity);
};

// request notes is actually info about the unit like address phone number, etc
const requestUnitInfo = unit => new Promise((resolve, reject) => {
  requestPromise(`/api/v1${unit}/notes`, { })
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
});

const requestUnitInfoThroughHashedApi = unit => new Promise((resolve, reject) => {
  getHashedApiToken()
  .then((hashedApiToken) => {
    requestPromise(`/api/v1/hashed${unit}/notes`, getHashedHeader(hashedApiToken))
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
  })
  .catch((error) => { reject(error) });
});

/**
 * The following code shows a lack of knowledge on ES6 features.
 * The code can be simplified and structured to follow the promise and resolve approach.
 * It can also benefit from the use of reduce, Object.assign, spread and rest features.
 *
const requestGroups = login
.then(() => axios.get('/api/v1/groups'))
.then(checkForError)
.then(getData)
.then((groups) => {
  const groupsMap = Object.create(null);
  console.log('Adding groups', groups);
  return axios.all(
    groups.map(
      group => axios.get(`/api/v1/resources?group=${group.group}`)
      .then(checkForError)
      .then(getData)
      .then((groupResources) => {
        console.log('Adding group', group, groupResources, 'to', groupsMap);
        groupsMap[group.group] = {};
        groupResources.forEach((resource) => {
          groupsMap[group.group][resource.resource] = resource.name;
        });
        return groupResources;
      })
      .catch((error) => {
        console.error(error);
      })
    )
  )
  .then(() => (groupsMap))
  .then((gMap) => {
    console.log('Fetched all groups', gMap);
    return gMap;
  });
});
*/

/**
 *
 * @type {moment.Moment}
 */
let groupsRetrievedAt = null;

/**
 *
 * @type {Promise<unknown>}
 */
let groupsPromise = null;

/**
 *
 * @type {Promise<unknown>}
 */
let hashedGroupsPromise = null;

/**
 *
 * @returns {Promise<unknown>}
 */
const requestGroups = () => {
  // Groups are valid if we fetched within one hour.
  const areGroupsStillValid = groupsRetrievedAt && groupsRetrievedAt.diff(moment(), 'hours', true) < 1;
  if (hashedGroupsPromise && areGroupsStillValid) {
    return hashedGroupsPromise;
  }
  if (groupsPromise && areGroupsStillValid) {
    return groupsPromise;
  }
  // If we reach this point then we must fetch the groups again.
  groupsRetrievedAt = moment();
  groupsPromise = new Promise((resolve, reject) => {
    const groupsUrl = '/api/v1/groups';

    axios.get(addTS(groupsUrl), { params: { includeResources: true } })
      .then((response) => {
        resolve(response.data);
      })
      .catch(error => reject(error));
  });
  return groupsPromise;
};

/**
 * Request the tickets for the logged in user.
 * @returns {Promise<any>}
 */
const requestTickets = () => new Promise((resolve, reject) => {
  axios.get(addTS('/api/v1/ticket'), { })
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
});

/**
 * Request the reservations for the logged in user.
 * @param from
 * @param to
 * @returns {Promise<any>}
 */
const requestReservations = (from, to) => new Promise((resolve, reject) => {
  let url = '/api/v1/reservations/all';
  if (from || to) {
    url = `${url}?`;
  }
  if (from) {
    url = `${url}from=${from}&`;
  }
  if (to) {
    url = `${url}to=${to}&`;
  }
  if (url.charAt(url.length - 1) === '&') {
    url = url.substr(0, url.length - 1);
  }
  axios.get(addTS(url), { })
    .then(checkForError)
    .then(getData)
    .then((data) => { resolve(data) })
    .catch((error) => { reject(error) });
});

/**
 *
 * @returns {Promise<unknown>}
 */
const requestGroupsThroughHashedApi = () => {
  // Groups are valid if we fetched within one hour.
  const areGroupsStillValid = groupsRetrievedAt && groupsRetrievedAt.diff(moment(), 'hours', true) < 1;
  if (groupsPromise && areGroupsStillValid) {
    return groupsPromise;
  }
  if (hashedGroupsPromise && areGroupsStillValid) {
    return hashedGroupsPromise;
  }
  // If we reach this point then we must fetch the groups again.
  groupsRetrievedAt = moment();
  hashedGroupsPromise = new Promise((resolve, reject) => {
    const groupsUrl = '/api/v1/hashed/groups';

    getHashedApiToken()
      .then((hashedApiToken) => {
        axios.get(addTS(groupsUrl), { params: { includeResources: true }, headers: { [hashedTokenHeaderName]: hashedApiToken } })
          .then((response) => {
            resolve(response.data);
          })
          .catch(error => reject(error));
      })
      .catch((error) => { reject(error) });
  });
  return hashedGroupsPromise;
};

const requestDedicatedResources = orgId => new Promise((resolve, reject) => {
  axios.get(addTS(`/api/v1/dedicated?orgId=${orgId}`))
    .then(checkForError)
    .then(getData)
    .then(data => resolve(data))
    .catch((error) => { reject(error) });
});

const getMunicipalities = lng => new Promise((resolve, reject) => {
  axios.get(`/api/v1/anonymous/municipalities?lng=${lng}`)
  .then(checkForError)
  .then(getData)
  .then(data => resolve(data))
  .catch((error) => { reject(error) });
});

const getRequestGroupsWrapper = (userIdentified) => {
  if (userIdentified) {
    return requestGroups();
  }
  return requestGroupsThroughHashedApi();
};

const getTimeSlotsPromise = (url) => {
  if (url.indexOf('hashed') > -1) {
    return getHashedApiToken()
      .then(hashedApiToken => requestPromise(url, getHashedHeader(hashedApiToken)));
  }
  return requestPromise(url, { });
};

const getTimeSlots = (params) => {
  console.log('Retrieving time slots', params);
  const { resources = [{}], from = moment(), to = moment().endOf('month'), responsibleResources, company, userIdentified, canAnonymousFetchTimes, legalEntity } = params;
  let dedicatedResources = {};

  if (responsibleResources.resources) {
    dedicatedResources = responsibleResources.resources;
  } else {
    responsibleResources.forEach((resource) => {
      dedicatedResources[resource.resource] = resource.name;
    });
  }

  const firstResource = resources.length ? resources[0] : {};
  let specialityParam = '';
  if (resources[0].speciality && Array.isArray(resources[0].speciality)) {
    specialityParam = resources[0].speciality.map(s => `&speciality=${s}`).join('');
  } else if (resources[0].speciality) {
    specialityParam = `&speciality=${resources[0].speciality}`;
  }
  // const specialityParam = resources[0].speciality ? `&speciality=${resources[0].speciality}` : '';
  // const resourceParam = resources[0].resource ? `&resource=${resources[0].resource}` : ''; // could be a list of resources
  const companyParam = company ? `&company=${company}` : '';
  const homeUnitKey = params.homeUnitId ? `/units/${params.homeUnitId}` : null;
  let includedUnitsResources;
  let excludedUnitsResources;
  let includedGroupsResources;
  let excludedGroupsResources;
  let includedResources;
  // If there are multiple groups, we would need to perform multiple requests.
  // We'll rather do the filtering ourselves.
  const groupParam = (resources[0].inGroups && resources[0].inGroups.length === 1
    && resources[0].inGroups[0] !== 'dedicated' && resources[0].inGroups[0])
  ? `&group=${resources[0].inGroups[0]}`
  : '';
  const unitParam = (resources[0].inUnits && resources[0].inUnits.length === 1
    && resources[0].inUnits[0] !== 'dedicated')
    ? `&units=${resources[0].inUnits[0]}`
    : '';
  const resourcesParam = resources[0].inResources ?
    `&${resources[0].inResources.map(res => `resources=${res}`).join('&')}` : '';

  let windowFrom = from;
  let windowTo = to;
  if (params.fromTicketOpeningDate && params.toTicketClosingDate) {
    if (params.fromTicketOpeningDate.isAfter(from)) {
      windowFrom = params.fromTicketOpeningDate;
    }
    if (params.toTicketClosingDate.isBefore(to)) {
      windowTo = params.toTicketClosingDate;
    }
  }
  /** To show the first available time then use this if statement...
  if (windowFrom.isAfter(windowTo)) {
    windowTo = windowFrom;
  }
  */
  let urlPrefix = '/api/v1/timeslots';
  if (canAnonymousFetchTimes) {
    urlPrefix = '/api/v1/hashed/timeslots';
  }
  if (userIdentified && legalEntity) {
    urlPrefix = '/api/v1/timeslots/transformed';
  }
  const legalEntityParam = legalEntity ?
    `&legalEntity=${legalEntity}` :
    '';
  return getTimeSlotsPromise(`${urlPrefix}?showTimes=true&from=${
    windowFrom.format(acuteTimeFormat)
  }&to=${
    windowTo.format(acuteTimeFormat)
  }${
    specialityParam
  }${
    groupParam
  }${
    unitParam
  }${
    companyParam
  }${
    resourcesParam
  }${
    legalEntityParam
  }`)
  .then(checkForError)
  .then(getData)
  .then((data) => {
    const promises = [];
    console.log('Checking units', resources, data);
    if (firstResource.inUnits && firstResource.inUnits.length > 0) {
      includedUnitsResources = {};
      let matchingUnit = {};
      promises.push(getRequestUnitsWrapper(userIdentified, legalEntity).then((fetchedUnits) => {
        firstResource.inUnits.forEach((unit) => {
          // movendos ticket may have dedicated units
          if (unit === 'dedicated' && responsibleResources.units) {
            responsibleResources.units.forEach((rUnit) => {
              matchingUnit = _.find(fetchedUnits, { unit: rUnit });
              includedUnitsResources = { ...includedUnitsResources, [rUnit]: matchingUnit };
            });
          } else if (unit === 'dedicated' && params.homeUnitId) {
            matchingUnit = _.find(fetchedUnits, { unit: homeUnitKey });
            includedUnitsResources = { ...includedUnitsResources, [homeUnitKey]: matchingUnit };
          } else {
            matchingUnit = _.find(fetchedUnits, { unit });
            includedUnitsResources = { ...includedUnitsResources, [unit]: matchingUnit };
          }
        });
      }).catch(error => console.error(error)));
    }
    if (firstResource.notInUnits && firstResource.notInUnits.length > 0) {
      excludedUnitsResources = {};
      let matchingUnit = {};
      promises.push(getRequestUnitsWrapper(userIdentified, legalEntity).then((fetchedUnits) => {
        firstResource.notInUnits.forEach((unit) => {
          matchingUnit = _.find(fetchedUnits, { unit });
          excludedUnitsResources = { ...excludedUnitsResources, [unit]: matchingUnit };
        });
      }).catch(error => console.error(error)));
    }
    // Fetch groups, if necessary. Then filter based on results
    console.log('Checking groups', resources, data);
    if (firstResource.inGroups && (firstResource.inGroups.length > 1 || (
      firstResource.inGroups.length === 1 && firstResource.inGroups[0] === 'dedicated'
    ))) {
      includedGroupsResources = {};
      promises.push(getRequestGroupsWrapper(userIdentified).then((fetchedGroups) => {
        firstResource.inGroups.forEach((group) => {
          if (group === 'dedicated' && dedicatedResources) {
            includedGroupsResources = Object.assign({}, includedGroupsResources,
              dedicatedResources);
          } else {
            includedGroupsResources = Object.assign({}, includedGroupsResources,
              fetchedGroups[group]);
          }
        });
      })
      .catch((error) => {
        console.error(error);
      }));
    }
    if (firstResource.notInGroups && firstResource.notInGroups.length) {
      excludedGroupsResources = {};
      promises.push(getRequestGroupsWrapper(userIdentified)
      .then((fetchedGroups) => {
        firstResource.notInGroups.forEach((group) => {
          if (group === 'dedicated' && dedicatedResources) {
            excludedGroupsResources = Object.assign({}, excludedGroupsResources,
              dedicatedResources);
          } else {
            excludedGroupsResources = Object.assign({}, excludedGroupsResources,
              fetchedGroups[group]);
          }
        });
      })
      .catch((error) => {
        console.error(error);
      }));
    }
    // check for specific resources in the path
    console.log('Checking resources', resources, data);
    if (firstResource.inResources && firstResource.inResources.length > 0) {
      includedResources = {};
      promises.push(getRequestResourcesWrapper(userIdentified).then((fetchedResources) => {
        firstResource.inResources.forEach((resource) => {
          const selectedResource = _.find(fetchedResources, { resource });
          includedResources = {
            ...includedResources,
            ...(selectedResource && { [selectedResource.resource]: selectedResource })
          };
        });
      })
        .catch((error) => {
          console.error(error);
        }));
    }
    return Promise.all(promises).then(() => data);
  })
  .then((data) => {
    console.log('Filtering for timeType, unit and group', data, includedUnitsResources, includedGroupsResources, excludedUnitsResources, excludedGroupsResources, includedResources);
    const typeDoesNotMatch = (resourceType, timeSlotType) =>
      resourceType && timeSlotType && timeSlotType.indexOf && timeSlotType !== resourceType && timeSlotType.indexOf(`${resourceType}-`) === -1;
    const resourceTimeType = firstResource.timeType;
    const selected = data.filter((d) => {
      // Let's assume the speciality mathes now, if specified
      // let's check for what's easily available
      const negativeTypeMatch = Array.isArray(resourceTimeType) ?
        resourceTimeType.every(timeType => typeDoesNotMatch(timeType, d.type)) :
        typeDoesNotMatch(resourceTimeType, d.type);
      if (negativeTypeMatch) {
        // Allow for company-specific time types, for instance ACU-123456
        return false;
      }
      if (includedUnitsResources && !includedUnitsResources[d.unit]) {
        return false;
      }
      if (includedGroupsResources && !includedGroupsResources[d.resource]) {
        return false;
      }
      if (includedResources && !includedResources[d.resource]) {
        return false;
      }
      if (excludedUnitsResources && excludedUnitsResources[d.unit]) {
        return false;
      }
      if (excludedGroupsResources && excludedGroupsResources[d.resource]) {
        return false;
      }
      return true;
    });
    console.log('Filtered', includedUnitsResources, includedGroupsResources, includedResources, excludedGroupsResources, selected);
    const mappedByDate = Object.create(null);
    selected.forEach((resource) => {
      const timesByDate = Object.create(null);
      resource.times.forEach((time) => {
        // Using the same as in files using the data. Should use a constant!
        const day = moment(time).format('YYYY-MM-DD');
        if (!timesByDate[day]) {
          timesByDate[day] = [];
        }
        timesByDate[day].push(time);
      });
      Object.keys(timesByDate).forEach((date) => {
        const resourceCopy = Object.assign({}, resource);
        resourceCopy.times = timesByDate[date];
        if (!mappedByDate[date]) {
          mappedByDate[date] = [];
        }
        mappedByDate[date].push(resourceCopy);
      });
    });
    console.log('Filtered and mapped by date', mappedByDate);
    if (Object.keys(mappedByDate).length === 0) {
      // No data found
      mappedByDate['2017-01-01'] = [];
    }
    return mappedByDate;
  });
};

// previous format:
// const rootPath = '/api/v1/anonymous/resource/root.json';
// const patchPath = '/api/v1/anonymous/resource/staging_config.json';
// new proposed format:
const rootPath = '/api/v1/anonymous/resource?file=root.json';
const patchPath = '/api/v1/anonymous/resource?file=staging_config.json';
const api = {
  loadRoot: () => axios.get(addTS(`${rootPath}`))
    .then(checkForError)
    .then(getData),
  // Because Acute staging (koeajo) configs are different (especially groups IDs) override defined parameters from production settings for staging
  loadPath: (root, employerId) => {
    const env = process.env.NODE_ENV;
    return new Promise((resolve, reject) => {
      axios.get(addTS(addSelectedEmployer(employerId, root.url)))
        .then(checkForError)
        .then(getData)
        .then((config) => {
          // dev and staging require fetching the additional configuration
          if (env === 'dev' || env === 'staging') {
            axios.get(addTS(`${patchPath}`))
              .then((response) => {
                if (response.status === 404) {  // S3 localization file does not exist => keep fully the local one
                  response.data = {};
                  return response;
                }
                if (response.status < 200 || response.status >= 300) {
                  const error = new Error(response.statusText);
                  error.response = response;
                  reject(error);
                }
                return response;
              })
              .then(getData)
              .then((stagingConfig) => {
                console.log('Acute staging config fetched: ', stagingConfig);
                resolve(applyAcuteStagingConfigToConfig(config, stagingConfig));
              });
          } else {
            // for production there is no need to fetch and apply another configuration
            resolve(config);
          }
        });
    });
  },
  checkUser,
  identifyUser: (payload) => {
    console.log(payload);
    const { firstName, lastName, personId, requiresEmployer } = payload;
    const data = `firstName=${
      encodeURIComponent(firstName)
    }&lastName=${
      encodeURIComponent(lastName)
    }&sotu=${
      encodeURIComponent(personId)
    }&requiresEmployer=${
      encodeURIComponent(requiresEmployer)
    }`;
    return axios.post('/login', data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(checkForError)
    .then(getData);
  },
  registerUser: jsonBody => new Promise((resolve, reject) => {
    getHashedApiToken()
    .then((hashedApiToken) => {
      axios.post('api/v1/hashed/registration', jsonBody, getHashedHeader(hashedApiToken))
        .then(checkForError)
        .then(getData)
        .then((data) => { resolve(data) })
        .catch((error) => { reject(error) });
    })
    .catch((error) => { reject(error) });
  }),
  updateUser: jsonBody => new Promise((resolve, reject) => {
    axios.post('/api/v1/account/ehr', jsonBody)
        .then(checkForError)
        .then(getData)
        .then((data) => { resolve(data) })
        .catch((error) => { reject(error) });
  }), 
  requestAuthentication: (method, createUserIfNotExists) => {
    const jsonData = { method, createUserIfNotExists };
    return axios.post('/api/v1/anonymous/authenticate/external', jsonData)
      .then(checkForError)
      .then(getData);
  },
  requestOrganization: () => requestOrganization,
  requestAllByLanguages,
  requestResources,
  requestUnits: legalEntity => requestUnits(legalEntity),
  requestUnitInfo,
  requestGroups,
  requestTimeSlots: params => getTimeSlots(params),
  requestTickets,
  requestReservations,
  storeReservation: (params) => {
    console.log('Making a reservation', params);
    return axios.post('/api/v1/reservations', params)
    .then(checkForError)
    .then(getData);
  },
  fetchReservation: ({ reservationCode }) =>
    axios.get(addTS(`/api/v1/reservations?code=${reservationCode}`))
  .then(checkForError)
  .then(getData),
  cancelReservation: ({ reservationId }) =>
    axios.delete(`/api/v1${reservationId}`)
  .then(checkForError)
  .then(getData),
  checkSession: () => Promise.resolve(true),
  /*
  checkSession: () => axios.head('/api/v1/groups')
  .then((response) => {
    if (response.status === 401) {
      // Only react to status if it is explicitly 401.
      // Other network errors may occur, and don't mean the session is expired.
      const error = new Error('Login expired!');
      error.response = response;
      throw error;
    }
    return response;
  }),
  */
  clearSession: () => axios.post('/logout', {}, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }),
  requestResourcesThroughHashedApi,
  requestUnitsThroughHashedApi: legalEntity => requestUnitsThroughHashedApi(legalEntity),
  requestUnitInfoThroughHashedApi,
  requestGroupsThroughHashedApi,
  requestDedicatedResources,
  getMunicipalities
};

export default api;



// WEBPACK FOOTER //
// ./src/api/api.js